export const defaultQueryStaleTimeMs = 30_000;
export const defaultQueryCacheTimeMs = 5 * 60_000;

type QueryEntry<T> = {
  data?: T;
  updatedAt: number;
  source?: QueryCacheSource;
  inFlight?: {
    controller: AbortController;
    consumers: Set<symbol>;
    promise: Promise<T>;
    source: QueryCacheSource;
  };
};

export type QueryCacheSource =
  'query' | 'intent-prefetch' | 'idle-prefetch' | 'navigation-handoff' | 'session-hydrated';

export type CachedQuerySnapshot<T> = {
  data: T;
  fresh: boolean;
  updatedAt: number;
  source: QueryCacheSource;
};

export type AcquiredQuery<T> = {
  promise: Promise<T>;
  deduplicated: boolean;
  source: QueryCacheSource;
  release: () => void;
};

export type PrefetchedQuery<T> = {
  promise: Promise<T>;
  status: 'fresh' | 'started' | 'in-flight';
  source: Exclude<QueryCacheSource, 'query' | 'session-hydrated'>;
  cancel: () => void;
};

const entries = new Map<string, QueryEntry<unknown>>();

const entryFor = <T>(key: string) => entries.get(key) as QueryEntry<T> | undefined;

export const createAuthorityQueryKey = (input: {
  accountId: number | 'anonymous';
  activeRole: string;
  route: string;
  query: string;
}) =>
  [
    `account:${input.accountId}`,
    `role:${input.activeRole}`,
    `route:${input.route}`,
    `query:${input.query}`,
  ].join('|');

export const readCachedQuery = <T>(
  key: string,
  staleTimeMs = defaultQueryStaleTimeMs,
  now = Date.now(),
): CachedQuerySnapshot<T> | null => {
  const entry = entryFor<T>(key);
  if (!entry || !Object.hasOwn(entry, 'data')) return null;
  return {
    data: entry.data as T,
    fresh: now - entry.updatedAt < staleTimeMs,
    updatedAt: entry.updatedAt,
    source: entry.source ?? 'query',
  };
};

const pruneExpiredQueries = (now = Date.now()) => {
  for (const [key, entry] of entries) {
    if (!entry.inFlight && now - entry.updatedAt >= defaultQueryCacheTimeMs) {
      entries.delete(key);
    }
  }
};

export const acquireQuery = <T>(
  key: string,
  load: (signal: AbortSignal) => Promise<T>,
  options: { source?: QueryCacheSource } = {},
): AcquiredQuery<T> => {
  pruneExpiredQueries();
  const existing = entryFor<T>(key);
  const consumer = Symbol(key);
  if (existing?.inFlight) {
    existing.inFlight.consumers.add(consumer);
    let released = false;
    return {
      promise: existing.inFlight.promise,
      deduplicated: true,
      source: existing.inFlight.source,
      release: () => {
        if (released) return;
        released = true;
        const current = entryFor<T>(key)?.inFlight;
        current?.consumers.delete(consumer);
        if (current?.consumers.size === 0) current.controller.abort();
      },
    };
  }

  const entry: QueryEntry<T> = existing ?? { updatedAt: 0 };
  const controller = new AbortController();
  const consumers = new Set([consumer]);
  const promise = Promise.resolve().then(() => load(controller.signal));
  const inFlight = { controller, consumers, promise, source: options.source ?? 'query' };
  entry.inFlight = inFlight;
  entries.set(key, entry as QueryEntry<unknown>);

  void promise.then(
    (data) => {
      const current = entryFor<T>(key);
      if (current?.inFlight !== inFlight) return;
      current.data = data;
      current.updatedAt = Date.now();
      current.source = inFlight.source;
      delete current.inFlight;
    },
    () => {
      const current = entryFor<T>(key);
      if (current?.inFlight !== inFlight) return;
      delete current.inFlight;
      if (!Object.hasOwn(current, 'data')) entries.delete(key);
    },
  );

  let released = false;
  return {
    promise,
    deduplicated: false,
    source: inFlight.source,
    release: () => {
      if (released) return;
      released = true;
      const current = entryFor<T>(key)?.inFlight;
      current?.consumers.delete(consumer);
      if (current?.consumers.size === 0) current.controller.abort();
    },
  };
};

export const prefetchQuery = <T>(
  key: string,
  load: (signal: AbortSignal) => Promise<T>,
  options: {
    source: 'intent-prefetch' | 'idle-prefetch' | 'navigation-handoff';
    staleTimeMs?: number;
  },
): PrefetchedQuery<T> => {
  const cached = readCachedQuery<T>(key, options.staleTimeMs ?? defaultQueryStaleTimeMs);
  if (cached?.fresh) {
    return {
      promise: Promise.resolve(cached.data),
      status: 'fresh',
      source: options.source,
      cancel: () => undefined,
    };
  }
  const acquired = acquireQuery(key, load, { source: options.source });
  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    acquired.release();
  };
  const promise = acquired.promise.finally(release);
  return {
    promise,
    status: acquired.deduplicated ? 'in-flight' : 'started',
    source: options.source,
    cancel: release,
  };
};

export const seedCachedQuery = (
  key: string,
  data: unknown,
  options: { updatedAt?: number; source: QueryCacheSource },
) => {
  const existing = entryFor<unknown>(key);
  if (existing?.inFlight) return false;
  entries.set(key, {
    data,
    updatedAt: options.updatedAt ?? Date.now(),
    source: options.source,
  });
  return true;
};

export const consumeCachedQuerySource = (key: string) => {
  const entry = entries.get(key);
  const source = entry?.inFlight?.source ?? entry?.source ?? null;
  if (!entry || source === null || source === 'query') return source;
  if (entry.inFlight) entry.inFlight.source = 'query';
  else entry.source = 'query';
  return source;
};

export const invalidateCachedQuery = (key: string) => {
  const entry = entries.get(key);
  if (entry && Object.hasOwn(entry, 'data')) entry.updatedAt = 0;
};

export const clearQueryCacheForAccount = (accountId: number) => {
  const prefix = `account:${accountId}|`;
  for (const [key, entry] of entries) {
    if (!key.startsWith(prefix)) continue;
    entry.inFlight?.controller.abort();
    entries.delete(key);
  }
};

export const clearQueryCache = () => {
  for (const entry of entries.values()) entry.inFlight?.controller.abort();
  entries.clear();
};

export const queryCacheSize = () => entries.size;
